/** App-wide vocabulary and chrome: labels, navigation, preferences, tasks. */

export default {
	common: {
		// The colour picker's free hex field, for an exact brand colour.
		color: {
			custom: "Custom colour (hex)",
			customHint: "An exact colour, for a brand. It does not adapt to the dark theme.",
			// The picker's first row: light, dark, Clear and the custom swatch.
			base: "Base",
			// A stored hex on no swatch, shown checked at the start of that row.
			customSwatch: "Custom colour {hex}",
		},
		appName: "Notitia",
		tagline: "Model knowledge-intensive processes. Carry them out together.",
		/** The landing page's paragraph under the tagline: what Notitia is, for someone who has not used it. */
		lead: "Notitia is LiveReader's workflow system for knowledge-intensive work. In the Designer, experts capture what a process needs to know, the steps it takes and the decisions it makes. In the Operator, teams carry it out live and together — with language understanding and AI where they help, and people deciding where it counts.",
		actions: {
			back: "Back",
			save: "Save",
			discard: "Discard",
			cancel: "Cancel",
			create: "Create",
			close: "Close",
			edit: "Edit",
			duplicate: "Duplicate",
			delete: "Delete",
			remove: "Remove",
			removeNamed: "Remove {name}",
			add: "Add",
			undo: "Undo",
			clear: "Clear",
			more: "More actions",
			moreFor: "More actions for {name}",
		},
		viewState: {
			// Shown wherever a list comes back filtered from a previous visit, so a
			// short list never reads as missing data.
			restored: "Filters restored",
		},
		fields: {
			email: "Email",
			description: "Description",
			label: "Label",
			displayName: "Display name",
			language: "Language",
		},
		sidebar: {
			resize: "Resize this panel",
			expand: "Expand this panel",
			collapse: "Collapse this panel",
			close: "Close this panel",
			dockLeft: "Dock sidebar on the left",
			dockRight: "Dock sidebar on the right",
			// What the key does, rather than where it would move the sidebar next — the help
			// list names the binding, and the binding is the same whichever side it is on.
			flip: "Move the sidebar to the other side",
		},
		keys: {
			// The modifier's name on a non-Apple keyboard; see utils/modifierKeys.ts for why
			// this one needs translating and ⌘ and ⌥ do not.
			ctrl: "Ctrl",
		},
		shortcuts: {
			// ⌘F, listed in the help while a page search is on screen (utils/pageSearch.ts).
			pageSearch: "Search this page",
		},
		unknownUser: "Unknown user",
		toasts: {
			/* Accessible name for the notification region, which HabToastHost requires. */
			label: "Notifications",
		},
		status: {
			saved: "Changes saved.",
			loading: "Loading…",
			noResults: "No matches",
			cannotUndo: "This cannot be undone.",
		},
		// What a failed write says when the SERVER's own answer would be English prose naming
		// generator internals. Keyed by the API's stable error `code` — see `errorMessage`.
		errors: {
			dataobjectInUse:
				"Something in the model is still built on this dataobject — a process uses it, or an attribute has it as its type. Change those first.",
			attributeInUseByAxiom:
				"An axiom still reads this, so it can't be removed. Change or remove that axiom first.",
			dependencyProtected:
				"This is defined in another project (a dependency) and can't be changed here.",
			predefinedProtected: "This is predefined by Notitia and can't be changed.",
			actionManaged:
				"This action belongs to a question. Change what it writes on the question; here it can only be moved, renamed and recoloured.",
			dependencyHeld:
				"A dependency has changes waiting for your review. Open the dependency settings and re-resolve it before making changes that regenerate code.",
			// The reason is the server's own sentence, which names the activity and the action.
			dataobjectInUseByAction:
				"This dataobject can't be deleted: an action still uses it. {reason}",
			relationInUseByAction:
				"This relation can't be deleted: an action still uses it. {reason}",
			processInUseByAction:
				"This process can't be deleted: an action still refers to it. {reason}",
			// The four refusals that come with a list of places: the dialog or banner that asked shows
			// the list, so these say only what was refused. `{reason}` is the server's own sentence, which
			// names the places too, and is added only when there is no list to show.
			attributeInUse: "This attribute can't be removed: it is still used.",
			typeInUse: "This type can't be removed: it is still used.",
			typeValueInUse: "A value can't be removed from this type: it is still used.",
			processDataobjectInUseByAction:
				"This process entry can't be removed: an action still creates a dataobject from it.",
			notAnActor: "Only the people this was set up for may do that.",
			userNotInDataspace:
				"That person cannot reach this dataspace, so nothing in it can be shared with them.",
			projectImported:
				"This project was imported from another server: its releases can run here, but nothing in it can be edited.",
			invalidAlias:
				"That is not a valid alias: use letters a to z, digits and hyphens, at most 63 characters, and nothing that looks like an id.",
			aliasTaken: "This alias is already in use. Choose another.",
		},
		entities: {
			activities: "Activities",
			processes: "Processes",
			types: "Types",
			dataobjects: "Dataobjects",
			views: "Views",
		},
	},
	nav: {
		signIn: "Sign in",
		signOut: "Sign out",
		account: "Account",
		accountMenu: "Account menu for {name}",
		accountMenuPending:
			"Account menu for {name}, {n} pending invitation | Account menu for {name}, {n} pending invitations",
		invitations: "Invitations ({n})",
		home: "Projects",
		designer: "Designer",
		operator: "Operator",
		"control-center": "Control center",
		projects: "Projects",
		projectHome: "Home",
		informationModel: "Information model",
		types: "Datatypes",
		terminology: "Terminology",
		dataobjects: "Dataobjects",
		processModeling: "Process modelling",
		processes: "Processes",
		activities: "Activities",
		dataspaces: "Dataspaces",
		departments: "Departments",
		settings: "Settings",
		expandRail: "Expand navigation",
		collapseRail: "Collapse navigation",
		documentation: "Documentation",
		clearViewState: "Clear view state",
	},
	preferences: {
		appearance: "Appearance",
		panels: "Panels",
		/*
		  Full phrases, not bare adjectives. The Panels sheet holds TWO choice runs —
		  this one and the docking edge — separated by a divider and nothing else,
		  because HabMenu has no group headings. Each row has to say what it is about
		  on its own.
		*/
		autoCollapse: "Collapse panels automatically",
		keepPanelsOpen: "Keep panels open",
	},
	theme: {
		light: "Light",
		dark: "Dark",
		system: "System",
	},
	languages: {
		en: "English",
		de: "Deutsch",
	},
	backgroundTasks: {
		title: "Background tasks",
	},
};
